import { ComponentProps, Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useDraftGroupNameSync } from '@frontend/api-sms-draft';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationPhoneSelector } from '@frontend/location-phone-selector';
import { PersonPhoneSelector, type PersonPhoneSelectorState } from '@frontend/person-phone-selector';
import { useAvatarsWithPhotos } from '@frontend/photos';
import {
  AssociatedContactsListItem,
  BlockedIndicatorInline,
  GroupContactsDrawer,
  GroupThreadTitle,
  OptOutIndicatorInline,
  ThreadDrawer,
  ThreadPopoverDrawer,
  useGroupBlockedStatus,
  useGroupOptOutStatus,
} from '@frontend/thread-header';
import { GroupThreadContext, groupParticipantsToAvatarProps } from '@frontend/thread-header-context';
import { pendo } from '@frontend/tracking';
import { Button, CircularAvatarGroup, NakedButton, Text, usePopoverMenu } from '@frontend/design-system';
import { MiniChatHeader } from '../mini-chat-header';
import {
  associatedContactsBackButtonStyles,
  associatedContactsCountStyles,
  associatedContactsHeaderRowStyles,
  associatedContactsHeaderStyles,
  associatedContactsListStyles,
  buttonStyles,
  changeThreadLocationPhoneSelectorStyles,
  fullWidthTextStyles,
  groupContactsButtonStyles,
  groupContentStyles,
  groupIdentityHeaderStyles,
  groupLocationPhoneSelectorStyles,
  groupLocationRowStyles,
  groupTitleWrapperStyles,
  personPhoneSelectorBaseStyles,
  personPhoneSelectorMultiOptionPaddingStyles,
  personPhoneSelectorSingleOptionPaddingStyles,
  rowLabelStyles,
  threadDrawerStyles,
  groupTopRowStyles,
} from './thread-options-drawer.styles';
import { ThreadOptionsMenu } from './thread-options-menu';
import { ThreadOptionsSubmenu } from './thread-options-submenu';

export type MenuOption = 'change-thread' | 'associated-contacts' | 'associated-contacts-template';

export type ThreadOptionsDrawerState = {
  selectedMenu: MenuOption | undefined;
  setSelectedMenu: Dispatch<SetStateAction<MenuOption | undefined>>;
  isGroupThread?: boolean;
  setPrimaryContact: (person: {
    personId: string;
    firstName: string;
    preferredName: string;
    lastName: string;
  }) => Promise<void>;
  setContextContactId: (contactId: string) => void;
  hideOptions: MenuOption[];
  associatedContacts: Person[];
  personPhoneSelectorState: PersonPhoneSelectorState;
  locationPhoneSelectorState: Omit<ComponentProps<typeof LocationPhoneSelector>, 'onSelectLocationPhone'>;
  primaryContactId?: string;
};

type ThreadOptionsDrawerProps = ThreadOptionsDrawerState &
  Pick<ComponentProps<typeof ThreadDrawer>, 'open' | 'onClose' | 'closeButtonRef'> &
  Pick<
    ComponentProps<typeof MiniChatHeader>,
    'onSelectPersonPhone' | 'onSelectLocationPhone' | 'groupId' | 'threadId' | 'locationPhone' | 'isNew'
  > & {
    groupThreadContext?: GroupThreadContext;
    onChangeGroupParticipantId?: (personId: string) => void;
    groupMessagingEnabled?: boolean;
    /** When true and the thread is a group thread, opens the GroupContactsDrawer directly instead of the This Thread panel. */
    isSelectingContextContact?: boolean;
  };

export const ThreadOptionsDrawer = ({
  selectedMenu,
  setSelectedMenu,
  setPrimaryContact,
  setContextContactId,
  hideOptions,
  associatedContacts,
  personPhoneSelectorState,
  locationPhoneSelectorState,
  onSelectPersonPhone,
  onSelectLocationPhone: _onSelectLocationPhone,
  groupId,
  primaryContactId,
  threadId,
  isGroupThread = false,
  groupThreadContext,
  onChangeGroupParticipantId,
  locationPhone,
  isNew,
  groupMessagingEnabled,
  isSelectingContextContact,
  ...rest
}: ThreadOptionsDrawerProps) => {
  const { t } = useTranslation('mini-chat');
  const { mutate: updateGroupThreadName } = SMSDataV4.Mutations.useUpdateGroupThreadNameMutation({
    options: {
      onSuccess: (_response, request) => {
        pendo.track('group-thread-name-changed', {
          threadId: request.threadId,
          locationId: request.locationId,
          hasCustomName: !!request.groupName,
          source: 'nwx-mini-chat-drawer',
        });
      },
    },
  });
  const { syncDraftGroupName } = useDraftGroupNameSync({
    threadId,
  });
  const { updateScheduledThreadGroupName } = ManualSmsScheduledV1._QueryUpdaters.useQueryUpdaters();
  const [groupContactsDrawerOpenState, setGroupContactsDrawerOpenState] = useState(false);
  // Change-context clicks on a group thread should open the GroupContactsDrawer directly, bypassing the This Thread panel.
  const groupContactsDrawerOpen = groupContactsDrawerOpenState || (isGroupThread && !!isSelectingContextContact);
  const groupParticipants = groupThreadContext?.participants ?? [];
  const rawGroupAvatars = useMemo(() => groupParticipantsToAvatarProps(groupParticipants), [groupParticipants]);
  // Enrich with profile photos so CircularAvatarGroup shows images for contacts that have them.
  const groupAvatars = useAvatarsWithPhotos(rawGroupAvatars);

  const { hasOptedOutParticipants, optedOutParticipants } = useGroupOptOutStatus({
    participants: groupParticipants,
    locationId: groupId,
    enabled: isGroupThread && groupMessagingEnabled,
  });

  const { blockedParticipants } = useGroupBlockedStatus({
    participants: groupParticipants,
    locationId: groupId,
    enabled: isGroupThread && groupMessagingEnabled,
  });

  // Only show blocked indicator if there are blocked-only participants (not also opted-out)
  const optedOutPhoneSet = useMemo(
    () => new Set(optedOutParticipants.map((p) => p.phoneNumber).filter(Boolean)),
    [optedOutParticipants]
  );
  const hasBlockedOnlyParticipants = blockedParticipants.some(
    (p) => p.phoneNumber && !optedOutPhoneSet.has(p.phoneNumber)
  );
  const handleGroupNameSave = (name?: string) => {
    const newGroupName = name ?? '';
    syncDraftGroupName(newGroupName);
    if (isGroupThread) {
      updateScheduledThreadGroupName({ threadId, locationId: groupId, groupName: newGroupName });
    }
    updateGroupThreadName({ locationId: groupId, threadId, groupName: newGroupName });
  };

  const { primaryContact, otherContacts } = useMemo(
    () =>
      associatedContacts.reduce<{ primaryContact?: Person; otherContacts: Person[] }>(
        (acc, curr) => {
          if (!acc.primaryContact && curr.personId === primaryContactId) {
            acc.primaryContact = curr;
          } else if (curr.personId !== primaryContactId) {
            acc.otherContacts.push(curr);
          }

          return acc;
        },
        { otherContacts: [] }
      ),
    [associatedContacts, primaryContactId]
  );

  // We need to disable close on click outside of the drawer if the location phone selection menu is open, so we set up
  // the popover menu state here.
  const locationPhonePopoverState = usePopoverMenu({ placement: 'bottom-start' });

  const handleSelectLocationPhone: typeof _onSelectLocationPhone = _onSelectLocationPhone
    ? (...args) => {
        _onSelectLocationPhone(...args);
        locationPhonePopoverState.close();
        rest.onClose();
      }
    : undefined;

  if (!isGroupThread && hideOptions.length > 1) return null;

  const hasMoreThanOnePhoneOption = personPhoneSelectorState.allPhones.filter((phone) => phone.isValid).length > 1;

  // The GroupContacts button (used in both the top row and the identity header)
  const groupContactsButton =
    groupMessagingEnabled && isGroupThread ? (
      <NakedButton
        onClick={() => setGroupContactsDrawerOpenState(true)}
        trackingId='mini-chat-group-contacts-button'
        css={groupContactsButtonStyles}
      >
        <Icon name='family-small' />
        <Text size='small' weight='bold'>
          {groupParticipants.length}
        </Text>
      </NakedButton>
    ) : null;

  // Determine whether to use ThreadPopoverDrawer (when group messaging wetag is on)
  const usePopoverStyle = groupMessagingEnabled;

  const drawerContent =
    isGroupThread && groupMessagingEnabled ? (
      <div css={groupContentStyles}>
        {/* Top row: back button | "This Thread" | group contacts button */}
        <div css={groupTopRowStyles}>
          <Button
            iconName='back'
            variant='secondary'
            size='small'
            css={{ padding: 'var(--fabric-number-n-1-px)', height: 'auto', width: 'auto' }}
            onClick={rest.onClose}
          />
          <Text size='medium' weight='bold'>
            {t('This Thread')}
          </Text>
          {groupContactsButton}
        </div>

        {/* Identity section: avatars + group title */}
        <div css={groupIdentityHeaderStyles}>
          {groupAvatars.length > 0 ? (
            <CircularAvatarGroup avatars={groupAvatars} size='medium' />
          ) : (
            <Icon name='family-small' color='subdued' />
          )}
          <div css={groupTitleWrapperStyles}>
            <GroupThreadTitle
              participants={groupParticipants}
              groupName={groupThreadContext?.groupName}
              isEditable
              onGroupNameSave={handleGroupNameSave}
              optOutIndicator={
                hasOptedOutParticipants || hasBlockedOnlyParticipants ? (
                  <>
                    {hasOptedOutParticipants && (
                      <OptOutIndicatorInline
                        show={true}
                        tooltipLabel={t('One or more people in this group have opted out.')}
                      />
                    )}
                    {hasBlockedOnlyParticipants && (
                      <BlockedIndicatorInline
                        show={true}
                        tooltipLabel={t('One or more people in this group is blocked.')}
                      />
                    )}
                  </>
                ) : undefined
              }
            />
          </div>
        </div>

        {/* To row */}
        <div css={buttonStyles} data-trackingid='mini-chat-group-recipients-row'>
          <span css={rowLabelStyles}>
            <Text size='small' color='subdued'>
              {t('To')}
            </Text>
            <Icon name='cell-phone-small' color='subdued' size={12} />
            <Text size='small' weight='bold'>
              {t('{{count}} Recipients', { count: groupParticipants.length })}
            </Text>
          </span>
        </div>

        {/* From / location phone row */}
        <div css={groupLocationRowStyles}>
          {handleSelectLocationPhone && (
            <LocationPhoneSelector
              {...locationPhoneSelectorState}
              onSelectLocationPhone={handleSelectLocationPhone}
              css={groupLocationPhoneSelectorStyles}
              variant='normal'
              popoverState={locationPhonePopoverState}
            />
          )}
        </div>
      </div>
    ) : selectedMenu === undefined ? (
      <ThreadOptionsMenu
        key='thread-options-menu'
        threadId={threadId}
        groupId={groupId}
        locationPhone={locationPhoneSelectorState.selectedLocationPhone?.locationPhone ?? ''}
        isNew={isNew}
        setSelectedMenu={setSelectedMenu}
        hideOptions={hideOptions}
        associatedContactsCount={associatedContacts.length}
      />
    ) : selectedMenu === 'change-thread' ? (
      <ThreadOptionsSubmenu
        key='change-thread-menu'
        backLabel={t('Change Thread')}
        onBack={() => setSelectedMenu(undefined)}
      >
        <PersonPhoneSelector
          {...personPhoneSelectorState}
          onSelectPersonPhone={({ personPhone }) => {
            if (!personPhone) {
              return;
            }
            rest.onClose();
            onSelectPersonPhone(personPhone);
          }}
          css={[
            personPhoneSelectorBaseStyles,
            // the button already has padding if it has more than one option, so we reduce it here
            hasMoreThanOnePhoneOption
              ? personPhoneSelectorMultiOptionPaddingStyles
              : personPhoneSelectorSingleOptionPaddingStyles,
          ]}
          locationId={groupId}
          variant='normal'
        />
        {handleSelectLocationPhone && (
          <LocationPhoneSelector
            {...locationPhoneSelectorState}
            onSelectLocationPhone={handleSelectLocationPhone}
            css={changeThreadLocationPhoneSelectorStyles}
            variant='normal'
            popoverState={locationPhonePopoverState}
          />
        )}
      </ThreadOptionsSubmenu>
    ) : null;

  return (
    <>
      {usePopoverStyle ? (
        <ThreadPopoverDrawer
          closeLabel={t('Close')}
          {...rest}
          onClose={() => {
            // Guard against useOnClickOutside closing the drawer while a nested popover (e.g. location phone selector)
            // is open — clicking on portal-rendered popover items would otherwise unmount the submenu before the click
            // handler fires. This mirrors the closeOnClickOutside guard used on the ThreadDrawer branch.
            if (!locationPhonePopoverState.isOpen && !groupContactsDrawerOpen) {
              rest.onClose();
            }
          }}
          open={
            locationPhonePopoverState.isOpen ||
            (!groupContactsDrawerOpen && selectedMenu !== 'associated-contacts' && rest.open)
          }
          shiftedFromTop={40}
        >
          {drawerContent}
        </ThreadPopoverDrawer>
      ) : (
        <ThreadDrawer
          closeLabel={t('Close')}
          css={threadDrawerStyles}
          {...rest}
          open={
            locationPhonePopoverState.isOpen ||
            groupContactsDrawerOpen ||
            (selectedMenu !== 'associated-contacts' && rest.open)
          }
          closeOnClickOutside={!locationPhonePopoverState.isOpen && !groupContactsDrawerOpen}
        >
          {drawerContent}
        </ThreadDrawer>
      )}

      {isGroupThread && groupMessagingEnabled ? (
        <GroupContactsDrawer
          open={groupContactsDrawerOpen}
          onClose={() => {
            setGroupContactsDrawerOpenState(false);
            rest.onClose();
          }}
          participants={groupParticipants}
          locationId={groupId}
          compact
          onSelect={(personId) => {
            onChangeGroupParticipantId?.(personId);
            setGroupContactsDrawerOpenState(false);
            rest.onClose();
          }}
        />
      ) : (
        <ThreadPopoverDrawer
          open={selectedMenu === 'associated-contacts' || selectedMenu === 'associated-contacts-template'}
          onClose={() => {
            rest.onClose();
            setSelectedMenu(undefined);
          }}
          closeLabel={t('Close')}
          size='small'
        >
          <div css={associatedContactsHeaderStyles}>
            <div css={associatedContactsHeaderRowStyles}>
              <Button
                iconName='back'
                variant='secondary'
                size='small'
                css={associatedContactsBackButtonStyles}
                onClick={() => {
                  if (selectedMenu === 'associated-contacts-template') {
                    rest.onClose();
                  }
                  setSelectedMenu(undefined);
                }}
              />
              <Text size='medium' weight='bold'>
                {t('Contacts Under This Number')}
              </Text>
              <Text
                color='light'
                size='small'
                css={associatedContactsCountStyles}
                data-testid='mini-chat-associated-contacts-count'
              >
                {associatedContacts?.length ?? 1}
              </Text>
            </div>
            <Text size='small' color='light' textAlign='center' css={fullWidthTextStyles}>
              {t('Select to focus another contact...')}
            </Text>
          </div>
          <ul css={associatedContactsListStyles}>
            {!!primaryContact && (
              <AssociatedContactsListItem
                personId={primaryContact.personId}
                person={primaryContact}
                onSelect={() => {
                  setContextContactId(primaryContact.personId);
                  rest.onClose();
                }}
                onSetPrimaryContact={() => {
                  rest.onClose();
                }}
                isPrimary
                locationId={groupId}
                size='small'
              />
            )}
            {otherContacts.map((person) => (
              <AssociatedContactsListItem
                key={person.personId}
                personId={person.personId}
                person={person}
                onSelect={() => {
                  setContextContactId(person.personId);
                  rest.onClose();
                }}
                onSetPrimaryContact={() => {
                  setPrimaryContact({
                    personId: person.personId,
                    firstName: person.firstName ?? '',
                    preferredName: person.preferredName ?? '',
                    lastName: person.lastName ?? '',
                  });
                  rest.onClose();
                }}
                isPrimary={false}
                locationId={groupId}
                size='small'
              />
            ))}
          </ul>
        </ThreadPopoverDrawer>
      )}
    </>
  );
};

export { buttonStyles } from './thread-options-drawer.styles';
